import {
  useFindAllHardwareQuery,
  useFindAllSoftwareQuery,
  useFindAllSupportPurchasesQuery,
  useFindOneSupportPurchaseQuery,
  useRemoveSupportPurchaseMutation,
} from '../../api/api-hooks';
import { Grid, IconButton, TextField, Typography } from '@mui/material';
import { Autocomplete } from '@mui/material';
import { nullStringCheck } from '../../utils/stringManipulation';
import { Item } from '../../utils/sharedTypes';
import { Order } from '../../api/api-generated';
import useSupportPurchasesItemCreator from './useSupportPurchasesItemCreator';
import useSupportPurchasesItemUpdater from './useSupportPurchasesItemUpdater';
import React, { useEffect, useReducer } from 'react';
import {
  SupportPurchaseFilter,
  SupportPurchaseForm,
} from './supportPurchasesTypes';
import {
  getISOStringOneYearFromToday,
  truncateISOString,
} from '../../utils/dateTime';
import {
  supportPurchaseMiddleware,
  supportPurchaseValidator,
} from './supportPurchasesUtils';
import SupportPurchasesTable from './SupportPurchasesTable';
import ItemOptionsBarWithLinking from '../../components/ItemOptionsBarWithLinking';
import SoftwareTypeManagementButton from '../software-types/SoftwareTypeManagementButton';
import SupportPurchasesFilterBar from './SupportPurchasesFilterBar';
import { AddCircleOutline, DeleteOutline } from '@mui/icons-material';
import HardwareManagementButton from '../hardware/HardwareManagementButton';

interface Props {
  selectedSupportPurchases: Item[];
  setSelectedSupportPurchases: React.Dispatch<React.SetStateAction<Item[]>>;
  order: Order;
}

const initialFilters: SupportPurchaseFilter = {
  company: null,
  software: null,
};

const addFormInitialState: SupportPurchaseForm = {
  order: null,
  software: null,
  companyId: '',
  supportStartDate: truncateISOString(new Date().toISOString()),
  supportEndDate: truncateISOString(getISOStringOneYearFromToday()),
  hardware: [],
};

const SupportPurchasesOptionsBar = ({
  selectedSupportPurchases,
  setSelectedSupportPurchases,
  order,
}: Props) => {
  const [deleteSupportPurchase] = useRemoveSupportPurchaseMutation();
  const itemCreator = useSupportPurchasesItemCreator();
  const itemUpdater = useSupportPurchasesItemUpdater();
  const { data: allSoftwareTypes } = useFindAllSoftwareQuery({});
  const { data: allSupportPurchases } = useFindAllSupportPurchasesQuery({});
  const { data: allHardware } = useFindAllHardwareQuery({});
  const [filters, setFilters] = useReducer(
    (curVals: SupportPurchaseFilter, newVals: any) => ({
      ...curVals,
      ...newVals,
    }),
    initialFilters
  );
  useEffect(() => {
    addFormInitialState.order = order;
    addFormInitialState.companyId = order.companyId + '';
  }, [order]);
  if (
    allSoftwareTypes === undefined ||
    allSupportPurchases === undefined ||
    allHardware === undefined
  )
    return null;
  return (
    <>
      <ItemOptionsBarWithLinking
        selectedItems={selectedSupportPurchases}
        setSelectedItems={setSelectedSupportPurchases}
        handlerMiddleware={supportPurchaseMiddleware}
        itemValidator={supportPurchaseValidator}
        formInstructions='Software, hardware, support start date, and support end date are required.'
        addFormInitialState={addFormInitialState}
        itemName='Record'
        itemToLink={order}
        itemToLinkName='order'
        itemToLinkIdName='orderId'
        itemToLinkTitle='Order'
        tableCreator={(setSelectedSupportPurchases) => (
          <>
            <SupportPurchasesFilterBar
              filters={filters}
              setFilters={setFilters}
              initialFilters={initialFilters}
            />
            <SupportPurchasesTable
              setSelectedSupportPurchases={setSelectedSupportPurchases}
              extraFilters={{ order: order, excludeOrder: true }}
              filters={filters}
            />
          </>
        )}
        childrenCreator={(addFormState: SupportPurchaseForm, handler: any) => (
          <Grid container>
            <Grid container item xs direction='column' spacing={1}>
              <Grid item>
                <Autocomplete
                  onChange={(event, value) => {
                    handler(event, value, 'software');
                  }}
                  value={addFormState.software}
                  options={allSoftwareTypes}
                  getOptionLabel={(type) => type.type}
                  style={{ width: 300, marginTop: '.5rem' }}
                  renderInput={(params) => (
                    <TextField
                      {...params}
                      label='Select Software'
                      variant='outlined'
                    />
                  )}
                />
              </Grid>
              <Grid item>
                <SoftwareTypeManagementButton />
              </Grid>
              <Grid item>
                <Grid container alignItems='center' spacing={1}>
                  <Grid item>
                    <Typography>Support Start: </Typography>
                  </Grid>
                  <Grid item>
                    <TextField
                      type='date'
                      margin='dense'
                      name='supportStartDate'
                      value={truncateISOString(
                        nullStringCheck(addFormState.supportStartDate)
                      )}
                      onChange={handler}
                    />
                  </Grid>
                </Grid>
              </Grid>
              <Grid item>
                <Grid container alignItems='center' spacing={1}>
                  <Grid item>
                    <Typography>Support End: </Typography>
                  </Grid>
                  <Grid item>
                    <TextField
                      type='date'
                      margin='dense'
                      name='supportEndDate'
                      value={truncateISOString(
                        nullStringCheck(addFormState.supportEndDate)
                      )}
                      onChange={handler}
                    />
                  </Grid>
                </Grid>
              </Grid>
            </Grid>
            <Grid container item xs direction='column' spacing={1}>
              <Grid container item spacing={1}>
                <Grid item style={{ marginBottom: '.7rem' }}>
                  <Typography variant='subtitle1'>
                    Associated Hardware:
                  </Typography>
                </Grid>
                {addFormState.hardware.map((hardware, idx) => (
                  <Grid
                    item
                    container
                    spacing={1}
                    alignItems='center'
                    key={idx}
                    style={{ marginLeft: '1.5rem' }}
                  >
                    <Grid item>
                      <Autocomplete
                        onChange={(event, value) => {
                          handler(event, value, `hardware${idx}`);
                        }}
                        value={hardware}
                        options={allHardware.filter((item) =>
                          item.companies.some(
                            (company) =>
                              company.id === Number(addFormState.companyId)
                          )
                        )}
                        getOptionLabel={(hardware) => hardware.serial}
                        style={{ width: 300 }}
                        renderInput={(params) => (
                          <TextField
                            {...params}
                            label='Select Hardware'
                            variant='outlined'
                          />
                        )}
                      />
                    </Grid>
                    <Grid item>
                      <IconButton
                        aria-label='Remove hardware'
                        onClick={(event) =>
                          handler(event, idx, 'deleteHardware')
                        }
                        size='large'
                      >
                        <DeleteOutline color='error' />
                      </IconButton>
                    </Grid>
                  </Grid>
                ))}
              </Grid>
              <Grid item style={{ marginLeft: '3rem' }}>
                <IconButton
                  aria-label='Add hardware'
                  onClick={(event: React.MouseEvent) => {
                    handler(event, null, 'newHardware');
                  }}
                  size='large'
                >
                  <AddCircleOutline color='primary' />
                </IconButton>
              </Grid>
            </Grid>
            <Grid
              item
              container
              spacing={1}
              alignItems='center'
              style={{ marginTop: '1rem' }}
            >
              <Grid item>
                <Typography>
                  Can't find the hardware item you want to link to this record?{' '}
                </Typography>
              </Grid>
              <Grid item>
                <HardwareManagementButton />
              </Grid>
            </Grid>
          </Grid>
        )}
        itemCreator={itemCreator}
        itemGetter={useFindOneSupportPurchaseQuery}
        itemUpdater={itemUpdater}
        itemDeleter={deleteSupportPurchase}
      />
    </>
  );
};

export default SupportPurchasesOptionsBar;
